import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css'; // Using Dashboard CSS
import './EditProfile.css'; // Additional styles for forms
import { logPageView } from '../utils/analytics';

const API_BASE_URL = '/api';

function EditProfile() {
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('basic');
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [specialities, setSpecialities] = useState([]);

  // Profile form state
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    countryCode: '',
    phoneNumber: '',
    countryPracticing: '',
    specialtyId: '',
    specialty: '',
    medicalCouncilNumber: ''
  });

  // Education form state - array of education records
  const [educationData, setEducationData] = useState([{
    degreeName: '',
    collegeName: '',
    universityName: '',
    country: '',
    yearOfCompletion: ''
  }]);

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    checkSessionAndFetchData();
  }, []);

  const checkSessionAndFetchData = async () => {
    try {
      const sessionResponse = await fetch(`${API_BASE_URL}/doctor-auth/check-session`, {
        credentials: 'include'
      });
      const sessionData = await sessionResponse.json();

      if (!sessionData.authenticated) {
        navigate('/login');
        return;
      }

      await fetchProfile();
      await fetchEducation();
      await fetchSpecialities();
    } catch (error) {
      console.error('Session check error:', error);
      navigate('/login');
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/profile`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success && data.data) {
        setProfileData({
          fullName: data.data.fullName || '',
          email: data.data.email || '',
          countryCode: data.data.countryCode || '+91',
          phoneNumber: data.data.phoneNumber || '',
          countryPracticing: data.data.countryPracticing || '',
          specialtyId: data.data.specialtyId || '',
          specialty: data.data.specialty || '',
          medicalCouncilNumber: data.data.medicalCouncilNumber || ''
        });
      }
    } catch (error) {
      console.error('Fetch profile error:', error);
      showAlert('Failed to load profile data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEducation = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/education`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success && data.data && data.data.length > 0) {
        setEducationData(data.data.map(edu => ({
          degreeName: edu.degreeName || '',
          collegeName: edu.collegeName || '',
          universityName: edu.universityName || '',
          country: edu.country || '',
          yearOfCompletion: edu.yearOfCompletion || ''
        })));
      } else {
        // If no education data, keep default single empty entry
        setEducationData([{
          degreeName: '',
          collegeName: '',
          universityName: '',
          country: '',
          yearOfCompletion: ''
        }]);
      }
    } catch (error) {
      console.error('Fetch education error:', error);
    }
  };

  const fetchSpecialities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/specialities`);
      const data = await response.json();
      if (data.success) {
        setSpecialities(data.data);
      }
    } catch (error) {
      console.error('Fetch specialities error:', error);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          fullName: profileData.fullName,
          countryCode: profileData.countryCode,
          phoneNumber: profileData.phoneNumber,
          countryPracticing: profileData.countryPracticing,
          specialtyId: profileData.specialtyId
        })
      });

      const data = await response.json();

      if (data.success) {
        showAlert('Profile updated successfully!', 'success');
        await fetchProfile(); // Refresh profile data
      } else {
        showAlert(data.message || 'Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      showAlert('Failed to update profile. Please try again.', 'error');
    }
  };

  const handleEducationSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/education`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          education: educationData
        })
      });

      const data = await response.json();

      if (data.success) {
        showAlert('Education details updated successfully!', 'success');
        await fetchEducation(); // Refresh education data
      } else {
        showAlert(data.message || 'Failed to update education details', 'error');
      }
    } catch (error) {
      console.error('Education update error:', error);
      showAlert('Failed to update education details. Please try again.', 'error');
    }
  };

  const addEducationEntry = () => {
    setEducationData([...educationData, {
      degreeName: '',
      collegeName: '',
      universityName: '',
      country: '',
      yearOfCompletion: ''
    }]);
  };

  const removeEducationEntry = (index) => {
    if (educationData.length > 1) {
      const newEducationData = educationData.filter((_, i) => i !== index);
      setEducationData(newEducationData);
    }
  };

  const updateEducationEntry = (index, field, value) => {
    const newEducationData = [...educationData];
    newEducationData[index][field] = value;
    setEducationData(newEducationData);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showAlert('New passwords do not match', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      showAlert('New password must be at least 6 characters long', 'error');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/doctor-auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        showAlert('Password changed successfully!', 'success');
        // Clear password fields
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        showAlert(data.message || 'Failed to change password', 'error');
      }
    } catch (error) {
      console.error('Password change error:', error);
      showAlert('Failed to change password. Please try again.', 'error');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/doctor-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'DR';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[parts.length - 1][0];
    }
    return parts[0][0];
  };

  if (loading) {
    return (
      <div className="dashboard edit-profile-page">
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard edit-profile-page">
      {/* Mobile Header */}
      <div className="mobile-header">
        <button className="mobile-menu-btn" onClick={() => setShowMobileMenu(true)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <img src="/logo.svg" alt="S.C.A.L.P.E.L." className="mobile-logo" />
      </div>

      {/* Mobile Overlay */}
      {showMobileMenu && (
        <div className="mobile-overlay" onClick={() => setShowMobileMenu(false)}></div>
      )}

      {/* Left Sidebar */}
      <div className={`left-sidebar ${showMobileMenu ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <img src="/logo.svg" alt="S.C.A.L.P.E.L." className="dashboard-logo" />
        </div>

        <button className="new-chat-btn" onClick={() => navigate('/dashboard')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Back to Dashboard</span>
        </button>

        <div className="sidebar-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input type="text" placeholder="Search" />
        </div>

        <div className="sidebar-divider"></div>

        <div className="sidebar-section">
          <div className="section-title">PROFILE SECTIONS</div>
          <div
            className={`recent-item ${activeSection === 'basic' ? 'active' : ''}`}
            onClick={() => setActiveSection('basic')}
          >
            <span>Basic Information</span>
          </div>
          <div
            className={`recent-item ${activeSection === 'education' ? 'active' : ''}`}
            onClick={() => setActiveSection('education')}
          >
            <span>Education Details</span>
          </div>
          <div
            className={`recent-item ${activeSection === 'password' ? 'active' : ''}`}
            onClick={() => setActiveSection('password')}
          >
            <span>Change Password</span>
          </div>
        </div>

        <div className="user-profile" onClick={() => setShowProfileMenu(!showProfileMenu)}>
          <div className="user-avatar">
            <span className="user-initials">{getInitials(profileData.fullName)}</span>
          </div>
          <div className="user-info">
            <div className="user-name">Dr. {profileData.fullName}</div>
            <div className="user-email">{profileData.specialty}</div>
          </div>
          {showProfileMenu && (
            <div className="profile-dropdown">
              <div className="profile-menu-item" onClick={() => navigate('/dashboard')}>
                <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Dashboard</span>
              </div>
              <div className="profile-menu-item" onClick={handleLogout}>
                <svg width="20" height="20" strokeWidth="1.4" viewBox="0 0 24 24" fill="none">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Same structure as Dashboard */}
      <div className="main-content">
        <div className="chat-container">
          {/* Alert */}
          {alert.show && (
            <div className={`alert alert-${alert.type}`}>
              {alert.message}
            </div>
          )}

          {/* Basic Information Form */}
          {activeSection === 'basic' && (
            <div className="form-card">
              <h2 className="form-card-title">Basic Information</h2>
              <form className="profile-form" onSubmit={handleProfileSubmit}>
                <div className="form-section">
                  <h3 className="form-section-title">Personal Information</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.fullName}
                        onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email (Cannot be changed)</label>
                      <input
                        type="email"
                        className="form-input"
                        value={profileData.email}
                        disabled
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Country Code</label>
                      <select
                        value={profileData.countryCode}
                        onChange={(e) => setProfileData({ ...profileData, countryCode: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="+91">+91 (India)</option>
                        <option value="+1">+1 (USA)</option>
                        <option value="+44">+44 (UK)</option>
                        <option value="+61">+61 (Australia)</option>
                        <option value="+971">+971 (UAE)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-input"
                        value={profileData.phoneNumber}
                        onChange={(e) => setProfileData({ ...profileData, phoneNumber: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="form-section-title">Professional Information</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Medical Council Number (Cannot be changed)</label>
                      <input
                        type="text"
                        className="form-input"
                        value={profileData.medicalCouncilNumber}
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Country Practicing</label>
                      <select
                        value={profileData.countryPracticing}
                        onChange={(e) => setProfileData({ ...profileData, countryPracticing: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="United States">United States</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                        <option value="UAE">UAE</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label className="form-label">Specialty</label>
                      <select
                        value={profileData.specialtyId}
                        onChange={(e) => setProfileData({ ...profileData, specialtyId: e.target.value })}
                        className="form-input"
                        required
                      >
                        <option value="">Select Specialty</option>
                        {specialities.map((spec) => (
                          <option key={spec.id} value={spec.id}>
                            {spec.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <button type="submit" className="submit-btn">
                  Save Changes
                </button>
              </form>
            </div>
          )}

          {/* Education Form */}
          {activeSection === 'education' && (
            <div className="form-card">
              <h2 className="form-card-title">Education Details</h2>
              <form className="profile-form" onSubmit={handleEducationSubmit}>
                {educationData.map((edu, index) => (
                  <div key={index} className="education-entry">
                    {educationData.length > 1 && (
                      <div className="education-entry-header">
                        <h3 className="form-section-title">Degree {index + 1}</h3>
                        <button
                          type="button"
                          className="remove-degree-btn"
                          onClick={() => removeEducationEntry(index)}
                        >
                          Remove
                        </button>
                      </div>
                    )}

                    <div className="form-section">
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Degree Name</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., MBBS, MD, MS"
                            value={edu.degreeName}
                            onChange={(e) => updateEducationEntry(index, 'degreeName', e.target.value)}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Year of Completion</label>
                          <input
                            type="number"
                            className="form-input"
                            placeholder="e.g., 2020"
                            min="1950"
                            max="2030"
                            value={edu.yearOfCompletion}
                            onChange={(e) => updateEducationEntry(index, 'yearOfCompletion', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group full-width">
                          <label className="form-label">College Name</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., All India Institute of Medical Sciences"
                            value={edu.collegeName}
                            onChange={(e) => updateEducationEntry(index, 'collegeName', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">University Name</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., Delhi University"
                            value={edu.universityName}
                            onChange={(e) => updateEducationEntry(index, 'universityName', e.target.value)}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Country</label>
                          <select
                            value={edu.country}
                            onChange={(e) => updateEducationEntry(index, 'country', e.target.value)}
                            className="form-input"
                            required
                          >
                            <option value="">Select Country</option>
                            <option value="India">India</option>
                            <option value="United States">United States</option>
                            <option value="United Kingdom">United Kingdom</option>
                            <option value="Australia">Australia</option>
                            <option value="UAE">UAE</option>
                            <option value="Canada">Canada</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <button type="button" className="add-degree-btn" onClick={addEducationEntry}>
                  + Add Another Degree
                </button>

                <button type="submit" className="submit-btn">
                  Save Education Details
                </button>
              </form>
            </div>
          )}

          {/* Password Form */}
          {activeSection === 'password' && (
            <div className="form-card">
              <h2 className="form-card-title">Change Password</h2>
              <form className="profile-form" onSubmit={handlePasswordSubmit}>
                <div className="form-section">
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label className="form-label">Current Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          className="form-input password-input"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          {showCurrentPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">New Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          className="form-input password-input"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Confirm New Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          className="form-input password-input"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <button type="submit" className="submit-btn">
                  Change Password
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditProfile;
